import { Injectable } from '@angular/core';

import { FileType } from '../models/file-item.model';
import { ConfigParserService } from '../services/config-parser.service';
import { WindowService } from '../services/window.service';
import { StringFormatter } from '../utilities/string-formatter.utility';
import { ContextService } from './context.service';

export const enum AppSpecificResource {
    CreateCAPErrorActionText = 'CreateCAPErrorActionText',
    CreateErrorActionText = 'CreateErrorActionText',
    CreateNewLocationPrompt = 'CreateNewLocationPrompt',
    ErrorSubtitleCreate = 'ErrorSubtitleCreate',
    ErrorSubtitleOneDriveDisabled = 'ErrorSubtitleOneDriveDisabled',
    ErrorSubtitleOpenNew = 'ErrorSubtitleOpenNew',
    ErrorTitleCreate = 'ErrorTitleCreate',
    ErrorTitleOpenNew = 'ErrorTitleOpenNew',
    FeaturedDocumentsTitle = 'FeaturedDocumentsTitle',
    FeaturedDocumentsShowMore = 'FeaturedDocumentsShowMore',
    FeaturedDocumentsShowMoreAriaLabel = 'FeaturedDocumentsShowMoreAriaLabel',
    FeaturedDocumentsShowLess = 'FeaturedDocumentsShowLess',
    FeaturedDocumentsShowLessAriaLabel = 'FeaturedDocumentsShowLessAriaLabel',
    FeaturedDocumentsError = 'FeaturedDocumentsError',
    NoRecentTitle = 'NoRecentTitle',
    NoRecentSubtitle = 'NoRecentSubtitle',
    NoRecentSubtitleDragAndDrop = 'NoRecentSubtitleDragAndDrop',
    NoPinnedTitle = 'NoPinnedTitle',
    NoPinnedSubtitle = 'NoPinnedSubtitle',
    NoSharedWithMeTitle = 'NoSharedWithMeTitle',
    NoSharedWithMeSubtitle = 'NoSharedWithMeSubtitle',
    NoDiscoverTitle = 'NoDiscoverTitle',
    NoDiscoverSubtitle = 'NoDiscoverSubtitle'
}

const enum SupportedLocalizedDateTimeFormat {
    Default,
    Medium,
    Long,
    WeekdayDate,
    WeekdayDateLong,
    MonthDate,
    Weekday,
    Time
}

@Injectable({
    providedIn: 'root'
})
export class LocService {
    public AppGalleryHeroTitle: string;
    public AuthPageTitle: string;
    public AuthSearchButtonAriaLabelForFluent: string;
    public AuthSearchPlaceholderTextForFluent: string;
    public CloseAppGalleryAriaLabel: string;
    public CloseSearchResultsAriaLabel: string;
    public SearchResults: string;
    public WelcomeAfternoonNoName: string;
    public WelcomeAfternoonWithName: string;
    public WelcomeEveningNoName: string;
    public WelcomeEveningWithName: string;
    public WelcomeMorningNoName: string;
    public WelcomeMorningWithName: string;

    public DocumentAnalyticsSingleTotalView: string;
    public DocumentAnalyticsMultipleTotalViews: string;
    public DocumentAnalyticsAvgTimeSpent: string;
    public DocumentAnalyticsLessThanAvgTimeSpent: string;
    public DocumentAnalyticsAvgTimeSpentMinutes: string;
    public DocumentAnalyticsAvgCompletion: string;
    public DocumentAnalyticsGlancedViews: string;
    public DocumentAnalyticsSingleQuickRead: string;
    public DocumentAnalyticsMultipleQuickReads: string;
    public DocumentAnalyticsSingleDeepRead: string;
    public DocumentAnalyticsMultipleDeepReads: string;
    public DocumentAnalyticsTotalViewsAvgTimeSpentAndAvgCompletionFormat: string;
    public DocumentAnalyticsDetailedViewsInfoFormat: string;

    public DownloadMruItemActionTitle: string;
    public LearnMoreAboutApp: string;
    public OpenInAppActionTitle: string;
    public OpenInApp: string;
    public OpenInBrowser: string;
    public OpenInCalendar: string;
    public OpenInDesktopApp: string;
    public OpenFileLocationActionTitle: string;
    public OpenFileLocationActionWithLocationTitle: string;
    public DocumentDescriptiveAriaLabelTitle: string;
    public DocumentDescriptiveAriaLabelActivity: string;
    public DocumentDescriptiveAriaLabelNoActivity: string;
    public DocumentDescriptiveAriaLabelPrivate: string;
    public DocumentDescriptiveAriaLabelShared: string;
    public DocumentDescriptiveAriaLabelDateShared: string;
    public DocumentDescriptiveAriaLabelLastModified: string;
    public DocumentDescriptiveAriaLabelLastOpened: string;
    public DocumentDescriptiveAriaLabelAnalytics: string;
    public DocumentDescriptiveAriaLabelCreatedBy: string;
    public DocumentDescriptiveAriaLabelMoreMembers: string;
    public DocumentDescriptiveAriaLabelNumResponses: string;
    public DocumentTabAriaLabelFormat: string;
    public EdgeNoRecentSubtitle: string;
    public EdgeMruSectionTitleShort: string;
    public FormsExtraMembersSideNote: string;
    public MenuAriaLabel: string;
    public MruSectionTitleShort: string;
    public MruCreateLinkText: string;
    public MruCreateLinkAriaLabelDescription: string;
    public FluentNoSharepointTitle: string;
    public FluentNoSharepointSubtitle: string;
    public FluentNoOnedriveTitle: string;
    public FluentNoOnedriveSubtitle: string;
    public NoRecentTitle: string;
    public NoRecentSubtitle: string;
    public NoRecentSubtitleDragAndDrop: string;
    public NoFavoritedTitle: string;
    public NoFavoritedSubtitle: string;
    public NoPinnedTitle: string;
    public NoPinnedSubtitle: string;
    public NoSharedWithMeTitle: string;
    public NoSharedWithMeSubtitle: string;
    public NoDiscoverTitle: string;
    public NoDiscoverSubtitle: string;
    public NoRecentTitleExcel: string;
    public NoRecentTitlePowerPoint: string;
    public NoRecentTitleWord: string;
    public NoRecentTitleSway: string;
    public NoRecentSubtitleExcel: string;
    public NoRecentSubtitlePowerPoint: string;
    public NoRecentSubtitleWord: string;
    public NoRecentSubtitleSway: string;
    public NoPinnedTitleExcel: string;
    public NoPinnedTitlePowerPoint: string;
    public NoPinnedTitleWord: string;
    public NoPinnedTitleSway: string;
    public NoPinnedSubtitleExcel: string;
    public NoPinnedSubtitlePowerPoint: string;
    public NoPinnedSubtitleWord: string;
    public NoPinnedSubtitleSway: string;
    public NoSharedWithMeTitleExcel: string;
    public NoSharedWithMeTitlePowerPoint: string;
    public NoSharedWithMeTitleWord: string;
    public NoSharedWithMeTitleForms: string;
    public NoSharedWithMeSubtitleExcel: string;
    public NoSharedWithMeSubtitlePowerPoint: string;
    public NoSharedWithMeSubtitleWord: string;
    public NoSharedWithMeSubtitleForms: string;
    public NoDiscoverTitleExcel: string;
    public NoDiscoverTitlePowerPoint: string;
    public NoDiscoverTitleWord: string;
    public NoDiscoverSubtitleExcel: string;
    public NoDiscoverSubtitlePowerPoint: string;
    public NoDiscoverSubtitleWord: string;
    public MruErrorTitle: string;
    public MruErrorSubtitle: string;
    public OpenOneDrive: string;
    public MoreInOneDrive: string;
    public GoToOneDrive: string;
    public ShowMoreResults: string;
    public ShowLessResults: string;
    public ShowMoreMruResultsAriaLabel: string;
    public ShowLessMruResultsAriaLabel: string;
    public ShowMoreMruResultsUpdateAriaLabel: string;
    public ShowLessMruResultsUpdateAriaLabel: string;
    public TimeSentInTeams: string;
    public JustNow: string;
    public JustNowLowerCase: string;
    public NumberMinutesAgoShort: string;
    public NumberMinutesInShort: string;
    public NumberMinutesIn: string;
    public NumberHoursAgoShort: string;
    public TimeYesterday: string;
    public TimeYesterdayLowerCase: string;
    public TimeDayOfWeek: string;
    public Yesterday: string;
    public YesterdayLowerCase: string;
    public RecentDocumentsLandmarkAriaLabel: string;
    public AllSways: string;
    public AllSwaysLinkTooltip: string;
    public AllSwaysLinkAriaLabel: string;
    public SwayTransformedFromWord: string;

    public ThumbnailImageAltText: string;
    public ThumbnailWordType: string;
    public ThumbnailExcelType: string;
    public ThumbnailPowerPointType: string;
    public ThumbnailPdfType: string;
    public ThumbnailVisioType: string;
    public ThumbnailTextType: string;
    public ThumbnailProjectType: string;
    public ThumbnailPowerBIType: string;
    public ThumbnailOneNoteType: string;
    public ThumbnailSwayType: string;
    public ThumbnailFormsType: string;
    public ThumbnailProjectPortfolioType: string;
    public ThumbnailNewsType: string;

    public MruActionsMenuAriaDescription: string;
    public MruActionsMenuToolTip: string;
    public OpenActionTitle: string;
    public OpenOnlineActionTitle: string;
    public OpenInOutlookActionTitle: string;
    public SuggestedActionsSectionTitle: string;
    public SuggestedActionJoinMeeting: string;
    public SuggestedActionReply: string;
    public SuggestedActionViewChanges: string;
    public SuggestedActionViewTask: string;
    public RecommendedActionsSectionTitle: string;
    public PinnedDocumentsSectionTitleLowercase: string;
    public RecentDocumentsSectionTitleLowercase: string;
    public FavoritedDocumentSectionTitle: string;
    public RecommendedDocumentsSectionTitle: string;
    public ButtonNextPage: string;
    public ButtonPreviousPage: string;
    public RemoveDocumentActionTitle: string;
    public RemoveDocumentAriaTitle: string;
    public RecommendedScrollNextAriaAnnouncement: string;
    public RecommendedScrollPreviousAriaAnnouncement: string;
    public SharedDocumentsSectionTitleLowercase: string;
    public TrendingDocumentsSectionTitle: string;
    public ClassNoteBookTabTitle: string;
    public NoClassNotebooksTitleOneNote: string;
    public NoClassNotebooksSubTitleOneNote: string;
    public PinDocumentActionTitle: string;
    public UnpinDocumentActionTitle: string;
    public RemoveFromSharedWithMe: string;
    public ListViewToggleAriaLabel: string;
    public TileViewToggleAriaLabel: string;
    public ListViewToggleUpdateAriaLabel: string;
    public TileViewToggleUpdateAriaLabel: string;
    public TileViewToggleAriaTooltip: string;
    public ListViewToggleAriaTooltip: string;
    public ListViewMenuAriaLabel: string;
    public TileViewMenuAriaLabel: string;
    public ListViewMenuItemAriaLabel: string;
    public TileViewMenuItemAriaLabel: string;
    public ListTileMenuAriaTooltip: string;
    public ShareActionTitle: string;
    public CopyLinkActionTitle: string;
    public ShareControlLoadingText: string;
    public MoveToFolderControlLoadingText: string;
    public ShareControlErrorText: string;
    public MoveToFolderControlErrorText: string;
    public ShareControlTitleAriaLabel: string;
    public MoveToFolderControlTitleAriaLabel: string;
    public OnlyYouText: string;
    public SharedText: string;
    public IsNewTitle: string;
    public UploadFooter: string;

    public CreateButtonText: string;
    public CreateButtonTextWord: string;
    public CreateButtonTextPowerPoint: string;
    public CreateButtonTextExcel: string;
    public CreateButtonTextOneNote: string;
    public CreateFileTypeNameListsList: string;
    public CreateFileTypeNameYammerPost: string;
    public CreateFileTypeNamePowerBiReport: string;
    public CreateFileTypeNameWhiteboardWhiteboards: string;
    public CreateMenuLinkWordTitle: string;
    public CreateMenuLinkExcelTitle: string;
    public CreateMenuLinkPowerPointTitle: string;
    public CreateMenuLinkSwayTitle: string;
    public CreateMenuLinkStreamTitle: string;
    public CreateMenuLinkScreenRecordingTitle: string;
    public CreateMenuLinkOneNoteTitle: string;
    public CreateMenuLinkOneNotePageTitle: string;
    public CreateMenuLinkOneNoteClassNotebookTitle: string;
    public CreateMenuLinkListsTitle: string;
    public CreateMenuLinkYammerPostTitle: string;
    public CreateMenuLinkPowerBiTitle: string;
    public CreateMenuLinkWhiteboardTitle: string;
    public CreateMenuLinkVisioTitle: string;
    public CreateMenuLinkFluidAbpTitle: string;
    public CreateMenuLinkFluidTitle: string;
    public CreateMenuLinkFormsTitle: string;
    public CreateMenuLinkFormsQuizTitle: string;
    public CreateMenuLinkFormsQuizStartPageTitle: string;
    public CreateMenuLinkAriaLabelFormat: string;
    public CreateModalLinkWordTitle: string;
    public CreateModalLinkExcelTitle: string;
    public CreateModalLinkPowerPointTitle: string;
    public CreateModalLinkOneNoteTitle: string;
    public CreateModalLinkOneNotePageTitle: string;
    public CreateModalLinkOneNoteClassNotebookTitle: string;
    public CreateModalLinkFormsFormTitle: string;
    public CreateModalLinkFormsQuizTitle: string;
    public CreateModalLinkSwayTitle: string;
    public CreateModalLinkStreamTitle: string;
    public CreateModalLinkScreenRecordingTitle: string;
    public CreateModalLinkVisioTitle: string;
    public CreateModalLinkFluidAbpTitle: string;
    public CreateModalLinkFluidTitle: string;
    public CreateModalAllTemplatesButton: string;
    public CreateModalCreateSectionHeader: string;
    public CreateModalTemplatesSectionHeader: string;
    public CreateModalCloseButtonAriaLabel: string;
    public CreateFlyOutHeader: string;
    public CreateTemplatesHeaderText: string;
    public CreateTemplatesFilterButtonText: string;
    public CreateTemplatesSeeMoreText: string;
    public CreateTilesHeaderText: string;
    public CreateTilesOnHomeHeaderText: string;
    public CreateTilesOnHomeHeaderTextRefresh: string;
    public CreateTilesPosInList: string;
    public CreateTilesSeeMoreText: string;
    public TemplatesPivotDropdownButtonText: string;
    public TemplatesLinkAriaLabel: string;
    public PremiumTemplateAriaLabel: string;
    public NewWordDocumentFileName: string;
    public NewExcelWorkbookFileName: string;
    public NewPowerPointPresentationFileName: string;
    public NewOneNoteNoteBookFileName: string;
    public NewFormsSurveyFileName: string;
    public NewFormsQuizFileName: string;
    public NewVisioDrawingFileName: string;
    public CreateNewDocumentLoadingPageTitle: string;
    public SwayName: string;
    public WordName: string;
    public ExcelName: string;
    public PowerPointName: string;
    public OneNoteName: string;
    public VisioName: string;
    public MicrosoftSwayName: string;
    public FormsName: string;
    public FluidName: string;
    public PDFName: string;
    public StreamName: string;
    public YammerName: string;
    public ListsName: string;
    public PowerBiName: string;
    public WhiteboardName: string;
    public ClipchampName: string;
    public StreamCrossLinkText: string;
    public StreamCrossLinkTextForGccH: string;
    public ErrorTitleCreateExcel: string;
    public ErrorTitleCreateForms: string;
    public ErrorTitleCreatePowerPoint: string;
    public ErrorTitleCreateWord: string;
    public ErrorTitleCreateSway: string;
    public ErrorTitleCreateOneNote: string;
    public ErrorTitleCreateVisio: string;
    public ErrorTitleOpenFluid: string;
    public ErrorSubtitleCreateExcel: string;
    public ErrorSubtitleCreatePowerPoint: string;
    public ErrorSubtitleCreateWord: string;
    public ErrorSubtitleCreateSway: string;
    public ErrorSubtitleCreateOneNote: string;
    public ErrorSubtitleCreateVisio: string;
    public ErrorSubtitleOpenFluid: string;
    public ErrorTitleOneDriveProvisioning: string;
    public ErrorSubtitleOneDriveProvisioning: string;
    public ErrorSubtitleOneDriveStorageProvisioning: string;
    public ErrorTitleNoLicense: string;
    public ErrorTitleAppNotAvailable: string;
    public ErrorSubtitleNoLicense: string;
    public ErrorTitleNoLicenseVisio: string;
    public ErrorSubtitleNoLicenseVisio: string;
    public ErrorTitleConditionalAccess: string;
    public ErrorSubtitleConditionalAccess: string;
    public CreateErrorActionTextExcel: string;
    public CreateErrorActionTextForms: string;
    public CreateErrorActionTextPowerPoint: string;
    public CreateErrorActionTextWord: string;
    public CreateErrorActionTextSway: string;
    public CreateErrorActionTextOneNote: string;
    public CreateErrorActionTextVisio: string;
    public CreateCAPErrorActionTextExcel: string;
    public CreateCAPErrorActionTextForms: string;
    public CreateCAPErrorActionTextPowerPoint: string;
    public CreateCAPErrorActionTextWord: string;
    public CreateCAPErrorActionTextSway: string;
    public CreateCAPErrorActionTextOneNote: string;
    public CreateCAPErrorActionTextVisio: string;
    public ErrorTitleOpenNewExcel: string;
    public ErrorTitleOpenNewForms: string;
    public ErrorTitleOpenNewPowerPoint: string;
    public ErrorTitleOpenNewWord: string;
    public ErrorTitleOpenNewSway: string;
    public ErrorTitleOpenNewOneNote: string;
    public ErrorTitleOpenNewVisio: string;
    public ErrorSubtitleOpenNewExcel: string;
    public ErrorSubtitleOpenNewPowerPoint: string;
    public ErrorSubtitleOpenNewWord: string;
    public ErrorSubtitleOpenNewSway: string;
    public ErrorSubtitleOpenNewOneNote: string;
    public ErrorSubtitleOpenNewVisio: string;
    public ErrorTitleOpenLink: string;
    public ErrorSubtitleOpenLink: string;
    public CheckAccountStatus: string;
    public CheckAccountStatusAriaLabel: string;
    public Dismiss: string;
    public DismissDialogAriaLabel: string;
    public Copy: string;
    public ErrorTitleCAPAdditionalStep: string;
    public ErrorTitleConsentRequiredAdditionalStep: string;
    public ErrorSubtitleConsentRequiredAdditionalStep: string;
    public ErrorSubtitleCAPAdditionalStep: string;
    public ErrorSubtitleCAPShareAdditionalStep: string;
    public ErrorSubtitleCAPUploadAdditionalStep: string;
    public ErrorSubtitleCAPMessageOnly: string;
    public ButtonNextCAPAriaLabel: string;
    public LearnMore: string;
    public ButtonLearnMoreCAPAriaLabel: string;
    public ErrorTitleMSAGraphCapError: string;
    public ErrorSubtitleMSAGraphCapError: string;

    public ErrorTitleGeneric: string;
    public ErrorSubtitleGeneric: string;
    public ErrorTitleDocumentNotFound: string;
    public ErrorSubtitleDocumentNotFound: string;
    public ErrorTitleUnauthorized: string;
    public ErrorSubtitleUnauthorized: string;
    public ErrorTitleTimeout: string;
    public ErrorSubtitleTimeout: string;
    public ErrorTitleOffline: string;
    public ErrorSubtitleOffline: string;
    public ErrorTitleLoginRequired: string;
    public ErrorSubtitleLoginRequired: string;
    public ErrorActionButtonTextLoginRequired: string;
    public ErrorActionButtonAriaLabelLoginRequired: string;
    public ErrorTitleInvalidFilename: string;
    public ErrorSubtitleInvalidFilename: string;
    public ErrorTitleRenameFailed: string;
    public ErrorSubtitleRenameFailed: string;

    public BrandOneDrive: string;
    public BrandSharePoint: string;
    public FoldersAttributionAriaLabel: string;
    public FoldersAttributionTitle: string;
    public RecentFoldersTitle: string;
    public RecentFolderDescriptiveAriaLabelTitle: string;
    public RecentFolderDescriptiveAriaLabelLastOpened: string;
    public ShowMoreFoldersAriaLabel: string;
    public ShowLessFoldersAriaLabel: string;
    public Bytes: string;
    public Kilobytes: string;
    public Megabytes: string;
    public Gigabytes: string;
    public Terabytes: string;

    public FirstRunBackButtonText: string;
    public FirstRunDoneButtonText: string;
    public FirstRunNextButtonText: string;
    public FeatureCalloutCloseButtonAriaLabel: string;
    public CalloutDoneButtonText: string;
    public CalloutSecondaryButtonText: string;
    public CalloutProgressText: string;
    public CreateADocumentText: string;
    public BusinessAppsCalloutTitle: string;
    public BusinessAppsCalloutSubtitle: string;

    public FirstRunFindMoreAppsTitle: string;
    public FirstRunFindMoreAppsSubtitleConsumer: string;
    public FirstRunFindMoreAppsSubtitleCommercial: string;
    public FirstRunManageOrganizationTitle: string;
    public FirstRunManageSchoolTitle: string;
    public FirstRunManageOrganizationSubtitleNext: string;
    public FirstRunCreateHereTitle: string;
    public FirstRunCreateHereSubtitle: string;
    public FirstRunManageContentTitle: string;
    public FirstRunManageContentSubtitle: string;
    public FirstRunUploadExistingContentTitle: string;
    public FirstRunUploadExistingContentSubtitle: string;
    public FirstRunDownloadAppsTitle: string;
    public FirstRunDownloadAppsSubtitle: string;
    public FirstRunUpgradeAnyTimeTitle: string;
    public FirstRunUpgradeAnyTimeSubtitle: string;
    public FirstRunUpgradeAnyTimeSubtitleTemp: string;
    public FirstRunWelcomeBackTitle: string;
    public FirstRunWelcomeBackSubtitle: string;
    public FirstRunFindMoreAppsSubtitleReturningUser: string;
    public ExpandedCreateCapabilitiesTitle: string;
    public FirstRunExpandedCreateCapabilitiesSubittle: string;
    public FirstRunAdminCenterMovedTitle: string;
    public FirstRunAdminCenterMovedSubtitle: string;
    public FirstRunYoureAllSetTitle: string;
    public FirstRunYoureAllSetSubtitle: string;
    public FirstRunYoureAllSetSubtitleReturningUser: string;

    public NewUserShelfTitle: string;
    public NewUserShelfSubtitle: string;
    public NewUserShelfCreateNew: string;
    public NewUserShelfExploreApps: string;
    public NewConsumerExperienceTitle: string;
    public NewConsumerExperienceSubtitle: string;
    public NoRecentNewConsumerTitle: string;
    public NoRecentNewConsumerSubtitle: string;
    public NoRecentNewConsumerSubtitleWithNoUpload: string;

    public ImageAltTest: string;

    public FeedbackHeader: string;
    public FeedbackMinimizedAriaLabel: string;

    public TabLoadingAriaLabel: string;
    public MruTabActivationAriaLabel: string;
    public SharePointTabActivationAriaLabel: string;
    public CalendarTabActiviationAriaLabel: string;
    public AppGalleryTabActivationAriaLabel: string;

    public CreateNewLocationPromptExcel: string;
    public CreateNewLocationPromptPowerPoint: string;
    public CreateNewLocationPromptWord: string;
    public CreateNewLocationPromptOneNote: string;
    public CreateNewLocationPromptVisio: string;

    public OneDriveRootFolderName: string;

    public FeaturedDocumentsTitleSway: string;
    public FeaturedDocumentsShowMoreSway: string;
    public FeaturedDocumentsShowMoreAriaLabelSway: string;
    public FeaturedDocumentsShowLessSway: string;
    public FeaturedDocumentsShowLessAriaLabelSway: string;
    public FeaturedDocumentsErrorSway: string;

    public CreateFileDialogButtonText: string;

    public JoinTeamsMeeting: string;
    public RecommendedDocumentsCalloutTitle: string;
    public RecommendedDocumentsCalloutSubtitle: string;
    public OfficeFeedPostedBy: string;
    public OfficeFeedYourUpcomingMeeting: string;
    public OfficeFeedYourUpcomingMeetingStarted: string;
    public OfficeFeedYourUpcomingMeetingHappeningNow: string;

    public UploadToOneDriveTitle: string;
    public UploadToOneDriveMessage1: string;
    public UploadToOneDriveMessage1Favorite: string;
    public UploadToOneDriveMessage1Pin: string;
    public UploadToOneDriveMessage1Share: string;
    public UploadToOneDriveMessage2: string;
    public PwaUploadAndOpenMenuItem: string;
    public PwaUnableToUploadFileTitle: string;
    public PwaUnableToUploadFileMessage: string;
    public PwaUnableToUploadFileMessageRebrand: string;
    public PwaUnableToOpenFileLocationTitle: string;
    public PwaUnableToOpenFileLocationMessage: string;
    public PwaUnableToOpenFileLocationMessageRebrand: string;
    public PwaOpenFileSystemSettings: string;
    public PwaJumpListNewFileText: string;
    public ClosePopUp: string;
    public ContinueUpload: string;

    public ClassNotebookTabTitle: string;
    public NoClassNotebooksTitle: string;
    public NoClassNotebooksSubTitle: string;
    public SaveAClassNotebook: string;

    public CreateNewNoteBookNameAlreadyExist: string;
    public CreateNewNoteBookNameInvalidChars: string;
    public CreateNewNoteBookNameExceeds128Chars: string;
    public CreateNewNoteBookNameCannotStartWithSpace: string;

    public NoOwnedOneNoteItems: string;
    public NoOwnedOneNoteItemsMemo: string;
    public MyNotebooksTabTitle: string;

    public HomeTabTitle: string;
    public HomeTabAriaLabel: string;
    public SearchTabTitle: string;
    public SearchTabAriaLabel: string;
    public AppsTabTitle: string;
    public AppsTabAriaLabel: string;
    public ThirdPartyIconAriaLabel: string;

    public OfficeBrandShort: string;

    public FirstRunFreeConsumerGoPremium: string;
    public GoPremiumButtonText: string;
    public BenefitsButtonText: string;
    public BenefitsCalloutTitle: string;
    public BenefitsCalloutSubtitle: string;

    public ODSPProvisioningSetupInProgress: string;
    public ODSPProvisioningSetupInProgressSubtitle: string;
    public ODSPProvisioningTakingTooLong: string;
    public ODSPProvisioningTakingTooLongSubtitle: string;

    public AllFormsTabLabel: string;
    public NoAllFormsTitle: string;
    public NoAllFormsSubtitle: string;
    public NoAllGroupFormsTitle: string;
    public NoDeletedFormsTitle: string;
    public MyFormsHeroTitle: string;
    public GroupFormsTitle: string;
    public MyGroupsSectionTitle: string;
    public TotalFormsLabel: string;
    public RecycleBinTabLabel: string;
    public FormsMoveToRecycleBinMessageNew: string;

    public ShowMoreGroupsLabel: string;
    public ShowLessGroupsLabel: string;
    public NoRecentGroupsTitle: string;
    public NoRecentGroupsSubtitle: string;

    public MoveItemLabel: string;
    public MoveItemSublabel: string;
    public MoveButtonLabel: string;
    public ResponsesText: string;

    public GroupOpenArialLabel: string;
    public FormsCalloutMRU: string;
    public FormsCalloutMyForms: string;
    public FormsCalloutGroupForms: string;

    public MoveActionMenuLabel: string;
    public CopyActionMenuLabel: string;
    public DeleteActionMenuLabel: string;
    public RestoreActionMenuLabel: string;
    public AllMyFormsRedirectLabel: string;
    public AllMyFormsRedirectTooltip: string;

    public CommonFormErrorMessage: string;
    public TakendownFormErrorMessage: string;
    public AutoBlockedFormErrorMessage: string;
    public FormsCompromisedUserAlertMessage: string;
    public ErrorTitlePhishingForm: string;

    public PublicGroupAccessLabel: string;
    public PrivateGroupAccessLabel: string;

    public FluidUnsupportedBrowserHeader: string;
    public FluidUnsupportedBrowserLowerMessage: string;
    public BrandGoogleChrome: string;
    public BrandMicrosoftEdge: string;
    public BrandMozillaFirefox: string;
    public BrandSafari: string;
    public FluidGetLatestEdge: string;
    public FluidEdgeRecommended: string;
    public FluidCanvasIFrameTitle: string;
    public DocumentTitleVersionHistory: string;
    public ReadOnlyState: string;
    public FluidMobileUpsell365BodyText: string;
    public FluidMobileUpsellInstallButtonText: string;

    public LeftNavAllAppsTileToolTip: string;
    public LeftNavCatchUpTileToolTip: string;
    public LeftNavCreateTileToolTip: string;
    public LeftNavHomeTileToolTip: string;
    public LeftNavInitialCalloutHeader: string;
    public LeftNavInitialCalloutBody: string;
    public LeftNavAppsOnLeftCalloutHeader: string;
    public LeftNavAppsOnLeftCalloutBody: string;
    public LeftNavDocumentsCalloutHeader: string;
    public LeftNavDocumentsCalloutBody: string;
    public LeftNavCreateCalloutHeader: string;
    public LeftNavCreateCalloutBody: string;
    public LeftNavAllAppsCalloutHeader: string;
    public LeftNavAllAppsCalloutBody: string;
    public LeftNavDoneButton: string;
    public LeftNavNonWXPEmptyStateString: string;
    public LeftNavNonWXPEmptyStateSubtitleString: string;

    public TemplatesCollectionRecommended: string;
    public TemplatesCollectionThemesDesigner: string;
    public TemplatesCollectionResumesAndCoverLetters: string;
    public TemplatesCollectionHealthAndFitness: string;
    public TemplatesCollectionPapersAndReports: string;
    public TemplatesCollectionBusinessAndPresentations: string;
    public TemplatesCollectionListsAndPersonal: string;
    public TemplatesCollectionPublishingAndWriting: string;
    public TemplatesCollectionCalendars: string;
    public TemplatesCollectionCollegeTools: string;
    public TemplatesCollectionBackToSchool: string;
    public TemplatesCollectionDiagramsAndFlowcharts: string;
    public TemplatesCollectionPresentations: string;
    public TemplatesCollectionTraining: string;
    public TemplatesCollectionFlyers: string;
    public TemplatesCollectionInvoices: string;
    public TemplatesCollectionCharts: string;
    public TemplatesCollectionPlannersAndTrackers: string;
    public TemplatesCollectionLearning: string;
    public TemplatesCollectionLists: string;
    public TemplatesCollectionBudgets: string;
    public TemplatesCollectionSchedules: string;
    public TemplatesCollectionDesigner: string;
    public TemplatesCollectionSurveys: string;
    public TemplatesCollectionEducation: string;
    public TemplatesCollectionBusiness: string;
    public TemplatesCollectionReportsAndNotes: string;
    public TemplatesCollectionBrochures: string;

    public EdgeworthMeetingRecordingFileName: string;
    public EdgeworthUntitledFileName: string;
    public EdgeworthFirstCallout: string;
    public EdgeworthSecondCallout: string;
    public EdgeworthThirdCallout: string;
    public EdgeworthFilterPlaceholderText: string;
    public EdgeworthUploadButtonName: string;
    public EdgeworthClearTextAriaLabel: string;
    public EdgeworthClearFilterButtonName: string;
    public EdgeworthFilterItemsHeader: string;
    public EdgeworthTotalFilesCountLabel: string;
    public EdgeworthLoadingText: string;
    public EdgeworthLocalFilesCountLabel: string;
    public EdgeworthODSPFilesCountLabel: string;
    public EdgeworthEmailAttachmentFilesCountLabel: string;
    public EdgeworthFilesLastSyncedDateTimeLabel: string;
    public EdgeworthSyncDocumentsButtonLabel: string;
    public EdgeworthBackToTopButtonLabel: string;
    public EdgeworthAllPivotLabel: string;
    public EdgeworthMyRecentPivotLabel: string;
    public EdgeworthRecentPivotLabel: string;
    public EdgeworthSharedByYouLabel: string;
    public EdgeworthSharedPivotLabel: string;
    public EdgeworthOtherFileType: string;
    public EdgeworthMediaFileType: string;
    public EdgeworthMeetingFileType: string;
    public EdgeworthVideoFileType: string;
    public EdgeworthDocumentFilterType: string;
    public EdgeworthActivityFilterTypeHidden: string;
    public EdgeworthActivityFilterTypeEdits: string;
    public EdgeworthActivityFilterTypeComments: string;
    public EdgeworthActivityFilterTypeMentions: string;
    public EdgeworthActivityFilterTypeTasks: string;
    public EdgeworthSharedFilterTypeNotShared: string;
    public EdgeworthSharedFilterTypeSharedByOthers: string;
    public EdgeworthModifiedFilterTypeHeader: string;
    public EdgeworthAccessedFilterTypeHeader: string;
    public EdgeworthDateFilterTypePastMonth: string;
    public EdgeworthDateFilterTypePastWeek: string;
    public EdgeworthDateFilterTypeYesterday: string;
    public EdgeworthDateFilterTypeAnytime: string;
    public EdgeworthActivityFilterTypeHeader: string;
    public EdgeworthNameFilterTypeHeader: string;
    public EdgeworthSharedActivityFilterTypeHeader: string;
    public EdgeworthNewActivityAriaLabel: string;
    public EdgeworthFilePathTitle: string;
    public EdgeworthNoFilesTitle: string;
    public EdgeworthNoFilterResultsTitle: string;
    public EdgeworthNoFavoritedSubtitle: string;
    public EdgeworthNoFavoritedTitle: string;
    public EdgeworthNoResultsFoundTitle: string;
    public EdgeworthNoResultsFoundSubtitle: string;
    public EdgeworthNoResultsFoundWithFilterSubtitle: string;
    public EdgeworthNoResultsFoundWithFilterTitle: string;
    public EdgeworthNoSharedSubtitle: string;
    public EdgeworthNoSharedTitle: string;
    public DocumentDescriptiveAriaLabelLastInteractedWith: string;
    public EdgeworthFileFilterAriaLabel: string;
    public EdgeworthAccessedDateFilterAriaLabel: string;
    public EdgeworthModifiedDateFilterAriaLabel: string;
    public EdgeworthSharedByFilterAriaLabel: string;
    public EdgeworthActivityFilterAriaLabel: string;
    public EdgeworthFavoritesPivotLabel: string;
    public FavoriteActionTitle: string;
    public FavoriteActionTitleV2: string;
    public UnfavoriteActionTitle: string;
    public UnfavoriteActionTitleV2: string;
    public EdgeworthFilterSearchAriaLabel: string;
    public HideActionTitle: string;
    public UnhideActionTitle: string;
    public FiltersApplied: string;
    public NoFiltersApplied: string;
    public DisplayingNumberOfFiles: string;
    public FilterResultsFound: string;
    public MeetingArtifactsOverflowMenuTitle: string;
    public MeetingArtifactOverflowMenuAriaLabel: string;

    public TermsOfUseLink: string;
    public FormsPoll: string;
    public FormsAssignment: string;
    public FormsMoveToGroupMessage: string;
    public FormsCopyToMyFormsMessage: string;
    public FormsCopyToGroupMessage: string;
    public FormsRestoredMessage: string;
    public FormsPermanentlyDeletedMessage: string;

    public VisioBusinessWacTemplateLabel: string;
    public VisioFlowChartWacTemplateLabel: string;
    public VisioGeneralWacTemplateLabel: string;
    public VisioNetworkWacTemplateLabel: string;
    public VisioSoftwareWacTemplateLabel: string;
    public VisioLibraryTileTitle: string;

    public MeetNowButtonLabel: string;

    public AddToToDo: string;
    public ViewInToDo: string;
    public TheyAsked: string;
    public Done: string;
    public NotATask: string;
    public YouSaid: string;
    public YouAsked: string;

    public WacDocumentErrorTitle: string;
    public WacDocumentErrorSubtitle: string;
    public CorrelationIdLabel: string;

    public M365AppsTitle: string;
    public ManageMyAppsText: string;

    public SettingsTitle: string;

    public WelcomeGreetingNoName: string;
    public WelcomeGreetingWithName: string;
    public ComingSoonBannerTitle: string;
    public CopilotAppUsagePrompt: string;

    public supportsLocaleSpecifiers: boolean;
    private dateFormatOptionsMap: Map<SupportedLocalizedDateTimeFormat, Intl.DateTimeFormatOptions> | null = new Map();
    private intlDateFormatterMap?: Map<SupportedLocalizedDateTimeFormat, Intl.DateTimeFormat> | null;
    private intlNumberFormatter?: Intl.NumberFormat | null;
    private intlSupported: boolean = true;
    private readonly officeMarket: string;

    constructor(
        contextService: ContextService,
        configParserService: ConfigParserService,
        private readonly windowService: WindowService
    ) {
        this.officeMarket = contextService.sharedContext.officeMarket;
        this.setSupportsLocaleSpecifiers();

        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.Medium, { year: 'numeric', month: 'short', day: 'numeric' });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.Long, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.WeekdayDate, { day: 'numeric', weekday: 'short' });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.WeekdayDateLong, { day: 'numeric', weekday: 'long' });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.MonthDate, { month: 'short', day: 'numeric' });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.Weekday, { weekday: 'short' });
        this.dateFormatOptionsMap.set(SupportedLocalizedDateTimeFormat.Time, { hour: 'numeric', minute: 'numeric' });

        this.intlDateFormatterMap = new Map();
        this.tryInitializeIntlFormatter();

        const resourceStringsConfig = configParserService.parse<any>('resourceStrings');
        if (resourceStringsConfig) {
            Object.assign && Object.assign(this, resourceStringsConfig);
        }
    }

    public appResource(resource: AppSpecificResource, app: FileType): string {
        const key = `${resource}${FileType[app]}`;
        return this[key] || this[resource];
    }

    public format(locString: string, ...params: Array<string>): string {
        return StringFormatter.format(locString, ...params);
    }

    // Ex: 9/3/2010
    public localizedDateString(date: Date): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.Default);
    }

    // Ex: Sep 3, 2010
    public localizedDateStringMedium(date: Date): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.Medium);
    }

    // Ex: Friday, September 3, 2010
    public localizedDateStringLong(date: Date): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.Long);
    }

    // Ex: 3 Fri
    public localizedWeekdayDate(date: Date): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.WeekdayDate);
    }

    // Ex: 3 Friday
    public localizedWeekdayDateLong(date: Date): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.WeekdayDateLong);
    }

    // Ex: Sep 3
    public localizedMonthDate(date: Date): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.MonthDate);
    }

    // Don't use this if supportsLocaleSpecifiers is false
    // Ex: Fri
    // TODO: Might need to get localized day/month names from loc service if different capitalization is needed
    public localizedWeekday(date: Date): string {
        return this.formatDate(date, SupportedLocalizedDateTimeFormat.Weekday);
    }

    // Ex: 12:05 PM
    public formatTimeFromDate(date: Date): string {
        const timeFormat = SupportedLocalizedDateTimeFormat.Time;
        if (this.intlSupported && this.intlDateFormatterMap && (this.intlDateFormatterMap.has(timeFormat) || this.tryInitializeIntlFormatter(timeFormat))) {
            return this.intlDateFormatterMap.get(timeFormat).format(date);
        }

        if (this.supportsLocaleSpecifiers && this.dateFormatOptionsMap) {
            return date.toLocaleTimeString(this.officeMarket, this.dateFormatOptionsMap.get(SupportedLocalizedDateTimeFormat.Time));
        }

        return date.toTimeString();
    }

    public localizedNumberString(num: number): string {
        if (this.intlNumberFormatter) {
            return this.intlNumberFormatter.format(num);
        }

        if (this.supportsLocaleSpecifiers) {
            return num.toLocaleString(this.officeMarket);
        }

        return num.toString();
    }

    public appName(app: FileType): string {
        switch (app) {
            case FileType.Word:
                return this.WordName;
            case FileType.Excel:
                return this.ExcelName;
            case FileType.PowerPoint:
                return this.PowerPointName;
            case FileType.Sway:
                return this.SwayName;
            case FileType.OneNote:
                return this.OneNoteName;
            case FileType.Visio:
                return this.VisioName;
            case FileType.Forms:
            case FileType.FormsQuiz:
                return this.FormsName;
            case FileType.Stream:
                return this.StreamName;
            case FileType.Fluid:
            case FileType.FluidActivityBlueprint:
                return this.FluidName;
            case FileType.Clipchamp:
                return this.ClipchampName;
        }

        return '';
    }

    private tryInitializeIntlFormatter(format?: SupportedLocalizedDateTimeFormat): boolean {
        if (!this.intlSupported) {
            return false;
        }

        // If Intl is available, prefer to create and cache formatters for a performance boost formatting dates later.
        // Otherwise, new formatters will be created on each 'toLocaleDateString()' and will not cached by the browser
        try {
            if ('Intl' in this.windowService.nativeWindow) {
                if (format) {
                    this.intlDateFormatterMap.set(format, new Intl.DateTimeFormat(this.officeMarket, this.dateFormatOptionsMap.get(format)));
                    this.dateFormatOptionsMap.delete(format);
                    if (this.dateFormatOptionsMap.size === 0) {
                        this.dateFormatOptionsMap = null;
                    }
                } else {
                    // Initialize default formatters if format is not passed in
                    this.intlDateFormatterMap.set(SupportedLocalizedDateTimeFormat.Default, new Intl.DateTimeFormat(this.officeMarket));
                    this.intlNumberFormatter = new Intl.NumberFormat(this.officeMarket);
                }

                return true;
            }
        } catch (ex) {
            /* Don't do anything */
        }

        // In cases of partial support for `Intl`, treat as if `Intl` is not supported at all.
        this.intlSupported = false;
        this.intlDateFormatterMap = null;
        this.intlNumberFormatter = null;
        this.dateFormatOptionsMap = null;
        return false;
    }

    private formatDate(date: Date, format: SupportedLocalizedDateTimeFormat): string {
        if (this.intlSupported && this.intlDateFormatterMap && (this.intlDateFormatterMap.has(format) || this.tryInitializeIntlFormatter(format))) {
            return this.intlDateFormatterMap.get(format).format(date);
        }

        if (this.supportsLocaleSpecifiers) {
            if (format === SupportedLocalizedDateTimeFormat.Default) {
                return date.toLocaleDateString(this.officeMarket);
            }

            if (this.dateFormatOptionsMap) {
                const options = this.dateFormatOptionsMap.get(format);
                return date.toLocaleDateString(this.officeMarket, options);
            }
        }

        return date.toDateString();
    }

    private setSupportsLocaleSpecifiers() {
        // Safari and IE 10 do not correctly implement toLocaleString when locale is specified.
        const toLocaleStringSupportsLocales = () => {
            try {
                const num = 0;
                num.toLocaleString('i');
            } catch (e) {
                return e.name === 'RangeError';
            }

            return false;
        };

        this.supportsLocaleSpecifiers = toLocaleStringSupportsLocales();
    }
}
